import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const GIFT_VARIANT_ID = '48443552628977';
const GIFT_THRESHOLD = 15;
const SOURCE_ID = 'haus-sample-reward-guard';

class HausSampleRewardGuard {
  constructor() {
    this.isReconciling = false;
    this.pendingReconcile = false;
    this.abortController = new AbortController();
  }

  connect() {
    const { signal } = this.abortController;
    document.addEventListener(ThemeEvents.cartUpdate, (event) => this.handleCartUpdate(event), { signal });
    window.addEventListener('pageshow', () => this.scheduleReconcile(), { signal });
    this.scheduleReconcile();
  }

  handleCartUpdate(event) {
    if (event?.detail?.sourceId === SOURCE_ID || event?.detail?.data?.source === SOURCE_ID) return;
    this.scheduleReconcile();
  }

  scheduleReconcile() {
    window.clearTimeout(this.reconcileTimer);
    this.reconcileTimer = window.setTimeout(() => this.reconcile(), 120);
  }

  isEligibleSample(item) {
    if (String(item.id) === GIFT_VARIANT_ID) return false;
    if (item.properties?._haus_sample_builder !== 'true') return false;
    return !/^TZT000[1-6]-S$/i.test(String(item.sku || '').trim());
  }

  async reconcile() {
    if (this.isReconciling) {
      this.pendingReconcile = true;
      return;
    }

    this.isReconciling = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const sampleUnits = cart.items.reduce(
        (total, item) => total + (this.isEligibleSample(item) ? item.quantity : 0),
        0
      );
      const giftItem = cart.items.find(
        (item) => String(item.id) === GIFT_VARIANT_ID && Boolean(item.properties?._haus_sample_reward)
      );

      if (!giftItem) return;

      const targetQuantity = sampleUnits >= GIFT_THRESHOLD ? 1 : 0;
      if (giftItem.quantity === targetQuantity) return;

      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        ),
      ].slice(0, 5);
      const requestBody = {
        id: giftItem.key,
        quantity: targetQuantity,
        sections_url: window.location.pathname,
      };
      if (sectionIds.length) requestBody.sections = sectionIds.join(',');

      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      let result;
      try {
        result = await response.json();
      } catch {
        return;
      }
      if (!response.ok || result.errors || result.status) return;

      document.dispatchEvent(
        new CartUpdateEvent(result, SOURCE_ID, {
          source: SOURCE_ID,
          itemCount: result.item_count,
          sections: result.sections,
        })
      );
    } catch {
      return;
    } finally {
      this.isReconciling = false;
      if (this.pendingReconcile) {
        this.pendingReconcile = false;
        this.scheduleReconcile();
      }
    }
  }
}

new HausSampleRewardGuard().connect();
